import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useApiToken from '../../hooks/useApiToken';
import { fetchWorkRoles, fetchUsersByWorkRoleIds, fetchPermissions } from '../../api/client';

function normalizeArray(v) {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.items)) return v.items;
  if (v && Array.isArray(v.value)) return v.value;
  if (v && Array.isArray(v.data)) return v.data;
  return [];
}

const UtilizationDataContext = createContext(null);

export function UtilizationDataProvider({ children }) {
  const { token, loading: tokenLoading, error: tokenError } = useApiToken();

  const [permissions, setPermissions] = useState(null);

  const [workRoles, setWorkRoles] = useState([]);
  const [workRolesLoading, setWorkRolesLoading] = useState(false);
  const [workRolesError, setWorkRolesError] = useState(null);

  const [selectedRoleIds, setSelectedRoleIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rm.util.selectedRoleIds') || '[]'); } catch { return []; }
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [selectedUserIds, setSelectedUserIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rm.util.selectedUserIds') || '[]'); } catch { return []; }
  });

  const [weekStartIso, setWeekStartIso] = useState(() => getSundayIso(new Date()));
  const [projectFilter, setProjectFilter] = useState('');
  const [committed, setCommitted] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // Persist filter selections
  useEffect(() => {
    try { localStorage.setItem('rm.util.selectedRoleIds', JSON.stringify(selectedRoleIds)); } catch {}
  }, [selectedRoleIds]);

  useEffect(() => {
    try { localStorage.setItem('rm.util.selectedUserIds', JSON.stringify(selectedUserIds)); } catch {}
  }, [selectedUserIds]);

  // Load permissions once when token is available
  useEffect(() => {
    if (!token || tokenLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const perms = await fetchPermissions(token);
        if (!cancelled) setPermissions(perms || {});
      } catch {
        if (!cancelled) setPermissions({});
      }
    })();
    return () => { cancelled = true; };
  }, [token, tokenLoading]);

  // Load work roles once when token is available
  useEffect(() => {
    if (!token || tokenLoading) return;
    let cancelled = false;
    (async () => {
      try {
        setWorkRolesLoading(true);
        setWorkRolesError(null);
        const raw = await fetchWorkRoles(token);
        if (!cancelled) setWorkRoles(normalizeArray(raw));
      } catch (e) {
        if (!cancelled) setWorkRolesError(e);
      } finally {
        if (!cancelled) setWorkRolesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, tokenLoading]);

  // Load users whenever selected roles change
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setUsersLoading(true);
        const raw = await fetchUsersByWorkRoleIds(selectedRoleIds, token);
        if (!cancelled) setUsers(normalizeArray(raw));
      } catch (e) {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, selectedRoleIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolved member identifiers for the selected users
  const memberIdentifiers = useMemo(() => {
    if (!selectedUserIds.length) return [];
    return selectedUserIds.map(uid => {
      const u = users.find(x => {
        const id = x?.id ?? x?.identifier;
        return id != null && String(id) === String(uid);
      });
      return u?.identifier || null;
    }).filter(Boolean);
  }, [selectedUserIds, users]);

  function commit() {
    setCommitted(true);
  }

  function reset() {
    setCommitted(false);
  }

  function refresh() {
    setRefreshVersion(v => v + 1);
    setLastRefreshed(new Date());
  }

  // Auto-refresh every 30 minutes once committed
  useEffect(() => {
    if (!committed) return;
    const id = setInterval(() => {
      setRefreshVersion(v => v + 1);
      setLastRefreshed(new Date());
    }, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, [committed]);

  const canWrite = permissions?.resourceManagementReadWrite === true;

  const value = useMemo(() => ({
    token, tokenLoading, tokenError,
    canWrite,
    workRoles, workRolesLoading, workRolesError,
    selectedRoleIds, setSelectedRoleIds,
    users, usersLoading,
    selectedUserIds, setSelectedUserIds,
    memberIdentifiers,
    weekStartIso, setWeekStartIso,
    projectFilter, setProjectFilter,
    committed, commit, reset,
    refreshVersion, lastRefreshed, refresh,
  }), [
    token, tokenLoading, tokenError,
    canWrite,
    workRoles, workRolesLoading, workRolesError,
    selectedRoleIds, users, usersLoading, selectedUserIds,
    memberIdentifiers, weekStartIso, projectFilter, committed,
    refreshVersion, lastRefreshed,
  ]);

  return (
    <UtilizationDataContext.Provider value={value}>
      {children}
    </UtilizationDataContext.Provider>
  );
}

export function useUtilizationContext() {
  const ctx = useContext(UtilizationDataContext);
  if (!ctx) throw new Error('useUtilizationContext must be used within UtilizationDataProvider');
  return ctx;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Return the ISO date string (YYYY-MM-DD) for the Sunday of the week containing `date`. */
export function getSundayIso(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format a YYYY-MM-DD string for display, e.g. "Aug 3, 2025". */
export function formatWeekDisplay(sundayIso) {
  if (!sundayIso) return '';
  const sun = new Date(sundayIso + 'T00:00:00');
  const sat = new Date(sun);
  sat.setDate(sat.getDate() + 6);
  const fmt = d =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(sun)} – ${fmt(sat)}`;
}
